import { getAssignmentDisplayName } from 'scenes/ExpenseTracking/utils/assignmentDisplayName';
import {
  roundTo2Decimals,
  safeNumber,
} from 'scenes/ExpenseTracking/utils/calculateValues';
import { getUrakkaActualCost } from 'scenes/ExpenseTracking/utils/projectBudgetSummary';

/**
 * The dashboard's "Talousseuranta" charts, derived in loadProjectExpenseSummary
 * from the entries it already reckons the summary from (the expense tracking
 * page's littera computation, selectCostsByAssignmentAndCategory), so the
 * charts and the realized cost ("Toteutuneet kulut") follow one set of rules:
 * - buildExpenseSeries(getInvoiceCostParts(entries)): the invoices' cumulative
 *   cost (the 1×2's "Kustannukset kertyminä");
 * - getTopCostCategories(entries): the litteras the most was spent on (the
 *   1×3's "Suurimmat kustannuslajit").
 * Pure: no store, no requests. Adapted from the expense tracking redesign's
 * ProjectCharts/chartData.js (getInvoiceCostParts, buildCumulativeSeries).
 */

/** The most points the cumulative series has, its starting 0 among them. */
export const EXPENSE_SERIES_MAX_POINTS = 60;

/** How many litteras "Suurimmat kustannuslajit" lists. */
export const EXPENSE_TOP_CATEGORY_COUNT = 5;

/**
 * The series' buckets, finest first: the first that keeps the series within
 * EXPENSE_SERIES_MAX_POINTS is taken.
 */
export const EXPENSE_SERIES_BUCKETS = Object.freeze([
  'day',
  'week',
  'month',
  'quarter',
  'year',
]);

const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_PREFIX = /^\d{4}-\d{2}-\d{2}/;

// A day key ("2025-03-12") as UTC midnight, and back: the days are counted
// in UTC, so a daylight saving change neither repeats nor skips one.
const utcDay = (key) => {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};
const keyOfUtcDay = (date) => date.toISOString().slice(0, 10);

/**
 * The day an invoice is on: the date part of its `date` ("2025-03-12…"), as
 * the expense tracking page's own chart takes it; null for a missing or an
 * impossible date.
 * @param {Object} invoice
 * @returns {string|null}
 */
export const invoiceDayKey = (invoice) => {
  const date = invoice && invoice.date;
  if (typeof date !== 'string') return null;
  const match = DAY_PREFIX.exec(date);
  if (!match) return null;
  const parsed = utcDay(match[0]);
  return !Number.isNaN(parsed.getTime()) && keyOfUtcDay(parsed) === match[0]
    ? match[0]
    : null;
};

/**
 * The invoice costs the realized cost holds, one part a littera's share of an
 * invoice on the invoice's day: every entry's `ownInvoices` but the rejected
 * ones, each at its `sum` there — the selector's own share (the littera's
 * rows of an invoice sorted by its rows, its blocks of a split invoice, else
 * the invoice's `sum`), the very amounts the entries' `actualCost` adds up
 * (getInvoiceCost). Their total is the realized cost but for the work hours,
 * the other costs and the transfers, which have no day.
 * @param {Object[]} entries Object.values(selectCostsByAssignmentAndCategory)
 * @returns {Array<{day: (string|null), amount: number, assignment: string}>}
 */
export const getInvoiceCostParts = (entries) => {
  const parts = [];
  (entries || []).forEach((entry) => {
    (entry?.ownInvoices || []).forEach((invoice) => {
      if (!invoice || invoice.approvalStatus === 'REJECTED') return;
      parts.push({
        day: invoiceDayKey(invoice),
        amount: safeNumber(invoice.sum),
        assignment: entry.id,
      });
    });
  });
  return parts;
};

// The start of the bucket a UTC day is in (a week starts on Monday).
const bucketStart = (unit, date) => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  switch (unit) {
    case 'week':
      return new Date(date.getTime() - ((date.getUTCDay() + 6) % 7) * DAY_MS);
    case 'month':
      return new Date(Date.UTC(year, month, 1));
    case 'quarter':
      return new Date(Date.UTC(year, month - (month % 3), 1));
    case 'year':
      return new Date(Date.UTC(year, 0, 1));
    default:
      return date;
  }
};

// The start of the bucket `steps` buckets after (before, when negative) the
// one starting at `start`.
const shiftBucket = (unit, start, steps) => {
  const year = start.getUTCFullYear();
  const month = start.getUTCMonth();
  switch (unit) {
    case 'week':
      return new Date(start.getTime() + 7 * steps * DAY_MS);
    case 'month':
      return new Date(Date.UTC(year, month + steps, 1));
    case 'quarter':
      return new Date(Date.UTC(year, month + 3 * steps, 1));
    case 'year':
      return new Date(Date.UTC(year + steps, 0, 1));
    default:
      return new Date(start.getTime() + steps * DAY_MS);
  }
};

// How many buckets of a unit run from the first day to the last, both in.
const bucketCount = (unit, first, last) => {
  const from = bucketStart(unit, first);
  const to = bucketStart(unit, last);
  const months =
    (to.getUTCFullYear() - from.getUTCFullYear()) * 12 +
    (to.getUTCMonth() - from.getUTCMonth());
  switch (unit) {
    case 'week':
      return Math.round((to - from) / (7 * DAY_MS)) + 1;
    case 'month':
      return months + 1;
    case 'quarter':
      return months / 3 + 1;
    case 'year':
      return to.getUTCFullYear() - from.getUTCFullYear() + 1;
    default:
      return Math.round((to - from) / DAY_MS) + 1;
  }
};

/**
 * The finest bucket that lays the days from `first` to `last` on at most
 * `maxPoints` points, the starting 0 among them; 'year' past every other.
 * @param {string} first a day key
 * @param {string} last a day key
 * @param {number} [maxPoints]
 * @returns {'day'|'week'|'month'|'quarter'|'year'}
 */
export const getExpenseSeriesBucket = (
  first,
  last,
  maxPoints = EXPENSE_SERIES_MAX_POINTS
) =>
  EXPENSE_SERIES_BUCKETS.find(
    (unit) => bucketCount(unit, utcDay(first), utcDay(last)) + 1 <= maxPoints
  ) || 'year';

/**
 * The invoices' cumulative cost, compact enough to keep in the store: every
 * bucket (getExpenseSeriesBucket: days, else weeks, months, quarters, years)
 * from the first invoice's to the last one's, each point the running total
 * through the bucket's last day — the last point's through the last invoice's
 * day, so no point stands in the future — after a 0 on the day before the
 * first bucket, so that even one invoice draws a line. The buckets without
 * invoices are in it (the line runs flat there). Each point's total is
 * rounded to the cent; the parts without a day are left out (their cost is in
 * the realized cost, not on the line).
 * @param {Array<{day: (string|null), amount: number}>} parts getInvoiceCostParts()
 * @param {{maxPoints?: number}} [options]
 * @returns {{bucket: (string|null), points: Array<{date: string, cumulative: number}>}}
 *   `date` a day key; no dated part: `{bucket: null, points: []}`
 */
export const buildExpenseSeries = (
  parts,
  { maxPoints = EXPENSE_SERIES_MAX_POINTS } = {}
) => {
  const dated = (parts || []).filter((part) => part && part.day);
  if (dated.length === 0) return { bucket: null, points: [] };
  const days = dated.map((part) => part.day).sort();
  const first = days[0];
  const last = days[days.length - 1];
  const bucket = getExpenseSeriesBucket(first, last, maxPoints);

  const sums = new Map();
  dated.forEach((part) => {
    const key = keyOfUtcDay(bucketStart(bucket, utcDay(part.day)));
    sums.set(key, (sums.get(key) || 0) + safeNumber(part.amount));
  });

  const firstStart = bucketStart(bucket, utcDay(first));
  const lastStart = bucketStart(bucket, utcDay(last));
  const points = [
    {
      date: keyOfUtcDay(new Date(firstStart.getTime() - DAY_MS)),
      cumulative: 0,
    },
  ];
  let cumulative = 0;
  for (
    let start = firstStart;
    start <= lastStart;
    start = shiftBucket(bucket, start, 1)
  ) {
    cumulative += sums.get(keyOfUtcDay(start)) || 0;
    const end =
      start < lastStart
        ? keyOfUtcDay(
            new Date(shiftBucket(bucket, start, 1).getTime() - DAY_MS)
          )
        : last;
    points.push({ date: end, cumulative: roundTo2Decimals(cumulative) });
  }
  return { bucket, points };
};

// A reference that may be the referenced object itself.
const referenceId = (value) =>
  value && typeof value === 'object' ? value.id : value;

/**
 * A littera's name as the expense tracking page shows it: its number and its
 * name there (the alternative name where it has one), "12. Maalaus".
 * @param {Object} entry
 * @returns {string}
 */
export const getCostCategoryName = (entry) => {
  const name = getAssignmentDisplayName(entry);
  const number =
    entry && entry.number !== undefined && entry.number !== null
      ? String(entry.number).trim()
      : '';
  if (number && name) return `${number}. ${name}`;
  return name || number;
};

/**
 * The litteras the most was spent on, largest first: each top-level littera
 * (one with no parent among the entries) with the realized cost of it and of
 * every littera under it — each entry's own as the Urakka figures count it
 * (getUrakkaActualCost: its invoices, work hours, other costs and transfer),
 * so all of them add up to the realized cost. "Lajittelemattomat" (the costs
 * of no littera of the project) is one of them. Only costs over 0, at most
 * `count`; equal costs keep the entries' order.
 * @param {Object[]} entries Object.values(selectCostsByAssignmentAndCategory)
 * @param {number} [count]
 * @returns {Array<{id: string, name: string, cost: number}>}
 */
export const getTopCostCategories = (
  entries,
  count = EXPENSE_TOP_CATEGORY_COUNT
) => {
  const list = (entries || []).filter((entry) => entry && entry.id);
  const byId = new Map(list.map((entry) => [entry.id, entry]));
  // The top-level littera of an entry (a loop of parents ends where it
  // closes).
  const topOf = (entry) => {
    const seen = new Set([entry]);
    let current = entry;
    for (;;) {
      const parent = byId.get(referenceId(current.parent));
      if (!parent || seen.has(parent)) return current;
      seen.add(parent);
      current = parent;
    }
  };
  const costs = new Map();
  list.forEach((entry) => {
    const top = topOf(entry);
    costs.set(top.id, (costs.get(top.id) || 0) + getUrakkaActualCost(entry));
  });
  return [...costs.entries()]
    .map(([id, cost]) => ({
      id,
      name: getCostCategoryName(byId.get(id)),
      cost: roundTo2Decimals(cost),
    }))
    .filter((category) => category.cost > 0)
    .sort((a, b) => b.cost - a.cost)
    .slice(0, Math.max(0, count));
};
